import { useEffect, useRef, useState } from 'react';

type ScrollBatchOptions = {
    /** how many records there are altogether */
    total: number;
    /** how many more are drawn each time the reader reaches the end */
    batch?: number;
    /** how far below the fold the next batch comes in */
    prefetchMargin?: string;
};

/**
 * The client-side half of an endless list.
 *
 * Where useInfinitePages fetches the next page, this draws the next batch of
 * records that are in hand already — a set the page fetched whole because the
 * endpoint has no filters to narrow it with. Slice the records to `shown` and
 * put `sentinelRef` under them, through InfiniteFoot or on its own: reaching
 * the end brings in more, which is the same gesture as every other list.
 */
const useScrollBatch = ({ total, batch = 25, prefetchMargin = '360px' }: ScrollBatchOptions) => {
    const [shown, setShown] = useState(batch);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const hasMore = shown < total;

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasMore) return undefined;
        const observer = new IntersectionObserver(
            (entries) => { if (entries.some((entry) => entry.isIntersecting)) setShown((n) => n + batch); },
            { rootMargin: prefetchMargin },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasMore, batch]);

    return {
        /** how many to draw — never more than there are */
        shown: Math.min(shown, total),
        hasMore,
        sentinelRef,
    };
};

export default useScrollBatch;
